export class InputState {
  /** Camera-space strafe: −1 left … +1 right. */
  analogX = 0;
  /** World-up: −1 down … +1 up. */
  analogY = 0;
  /** Camera-space: −1 forward … +1 back. */
  analogZ = 0;
  sprint = false;

  reset(): void {
    this.analogX = 0;
    this.analogY = 0;
    this.analogZ = 0;
    this.sprint = false;
  }
}

export type Gesture =
  | { type: "drag"; dx: number; dy: number; pointers: number }
  | { type: "pinch"; scale: number; x: number; y: number }
  | { type: "rotate"; angle: number }
  | { type: "end" }
  | { type: "dbltap"; x: number; y: number };

type Pt = { x: number; y: number };

const TAP_MS = 280;
const TAP_PX = 14;
const DBL_MS = 340;
const PINCH_EPS = 0.02;
const ROTATE_EPS = 0.02;

let livePointers = 0;

/** Fingers/buttons currently down on the canvas. Wheel should ignore these. */
export function activeCanvasPointers(): number {
  return livePointers;
}

export class CanvasGestures {
  private pts = new Map<number, Pt>();
  private pinchDist = 0;
  private pinchAngle = 0;
  private pinchMid: Pt = { x: 0, y: 0 };
  private tapOrigin: { t: number; x: number; y: number } | null = null;
  private lastTap = 0;
  private lastTapAt: Pt = { x: 0, y: 0 };
  private moved = false;
  private suppressOne = false;
  private listening = false;
  private scaleHold = 1;
  private angleHold = 0;

  constructor(
    private canvas: HTMLCanvasElement,
    private onGesture: (g: Gesture) => void,
  ) {
    this.bind();
  }

  private bind(): void {
    const c = this.canvas;

    // Chrome Android synthesizes wheel events from two-finger drags and will
    // cancel pointers unless touch is absorbed here.
    const absorb = (e: TouchEvent) => e.preventDefault();
    c.addEventListener("touchstart", absorb, { passive: false });
    c.addEventListener("touchmove", absorb, { passive: false });
    c.addEventListener("touchend", absorb, { passive: false });
    document.addEventListener(
      "touchmove",
      (e) => {
        if (this.pts.size > 0) e.preventDefault();
      },
      { passive: false, capture: true },
    );

    c.addEventListener("pointerdown", (e) => this.onPtrDown(e, true));
    c.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private onPtrDown(e: PointerEvent, fromCanvas: boolean): void {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (this.pts.has(e.pointerId)) return;
    if (!fromCanvas && this.pts.size === 0) return;
    if (!fromCanvas && this.isHudControl(e.target)) return;

    e.preventDefault();
    this.pts.set(e.pointerId, this.localPtr(e));
    livePointers = this.pts.size;
    this.canvas.classList.add("is-dragging");
    this.armDoc();

    if (this.pts.size === 1) {
      const p = this.pts.get(e.pointerId)!;
      this.tapOrigin = { t: performance.now(), x: p.x, y: p.y };
      this.moved = false;
      if (e.pointerType === "mouse") this.canvas.setPointerCapture(e.pointerId);
    } else {
      this.tapOrigin = null;
      this.suppressOne = true;
      this.syncPinch();
    }
  }

  private isHudControl(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest("button, .stick, .sheet, input"));
  }

  private armDoc(): void {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener("pointerdown", this.onDocPtrDown, true);
    window.addEventListener("pointermove", this.onDocPtrMove);
    window.addEventListener("pointerup", this.onDocPtrUp);
    window.addEventListener("pointercancel", this.onDocPtrUp);
  }

  private disarmDoc(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener("pointerdown", this.onDocPtrDown, true);
    window.removeEventListener("pointermove", this.onDocPtrMove);
    window.removeEventListener("pointerup", this.onDocPtrUp);
    window.removeEventListener("pointercancel", this.onDocPtrUp);
  }

  private onDocPtrDown = (e: PointerEvent): void => {
    this.onPtrDown(e, false);
  };

  private onDocPtrMove = (e: PointerEvent): void => {
    const prev = this.pts.get(e.pointerId);
    if (!prev) return;
    e.preventDefault();
    const p = this.localPtr(e);
    this.pts.set(e.pointerId, p);
    this.emitMove(prev, p);
  };

  private onDocPtrUp = (e: PointerEvent): void => {
    this.endPointer(e.pointerId);
  };

  private emitMove(prev: Pt, p: Pt): void {
    if (this.pts.size === 1) {
      if (this.suppressOne) return;
      this.emitOneFinger(prev, p);
      return;
    }
    if (this.pts.size >= 2) this.emitTwoFinger();
  }

  private emitOneFinger(prev: Pt, p: Pt): void {
    const dx = p.x - prev.x;
    const dy = p.y - prev.y;
    if (Math.hypot(p.x - (this.tapOrigin?.x ?? p.x), p.y - (this.tapOrigin?.y ?? p.y)) > TAP_PX) {
      this.moved = true;
    }
    if (dx !== 0 || dy !== 0) this.onGesture({ type: "drag", dx, dy, pointers: 1 });
  }

  private emitTwoFinger(): void {
    const pair = this.pair();
    if (!pair) return;
    const dist = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y);
    const mid = {
      x: (pair[0].x + pair[1].x) / 2,
      y: (pair[0].y + pair[1].y) / 2,
    };
    const angle = Math.atan2(pair[1].y - pair[0].y, pair[1].x - pair[0].x);
    if (this.pinchDist > 0 && dist > 0) {
      const scale = dist / this.pinchDist;
      this.scaleHold *= scale;
      if (Math.abs(this.scaleHold - 1) >= PINCH_EPS) {
        this.onGesture({ type: "pinch", scale: this.scaleHold, x: this.pinchMid.x, y: this.pinchMid.y });
        this.scaleHold = 1;
      }
      const dx = mid.x - this.pinchMid.x;
      const dy = mid.y - this.pinchMid.y;
      if (dx !== 0 || dy !== 0) this.onGesture({ type: "drag", dx, dy, pointers: 2 });
      this.angleHold += wrapAngle(angle - this.pinchAngle);
      if (Math.abs(this.angleHold) >= ROTATE_EPS) {
        this.onGesture({ type: "rotate", angle: this.angleHold });
        this.angleHold = 0;
      }
    }
    this.pinchDist = dist;
    this.pinchMid = mid;
    this.pinchAngle = angle;
  }

  private endPointer(id: number): void {
    if (!this.pts.has(id)) return;
    this.pts.delete(id);
    livePointers = this.pts.size;
    const c = this.canvas;
    if (c.hasPointerCapture(id)) c.releasePointerCapture(id);

    if (this.pts.size < 2) this.finishTwoFinger();

    if (this.pts.size === 0) {
      c.classList.remove("is-dragging");
      this.pinchDist = 0;
      this.pinchAngle = 0;
      this.scaleHold = 1;
      this.angleHold = 0;
      this.suppressOne = false;
      this.disarmDoc();
      this.finishTap();
    } else {
      this.syncPinch();
      this.tapOrigin = null;
    }
  }

  private finishTwoFinger(): void {
    this.onGesture({ type: "end" });
    this.pinchDist = 0;
    this.scaleHold = 1;
    this.angleHold = 0;
  }

  private finishTap(): void {
    const origin = this.tapOrigin;
    this.tapOrigin = null;
    if (!origin || this.moved) return;
    const now = performance.now();
    if (now - origin.t > TAP_MS) return;
    if (
      now - this.lastTap < DBL_MS &&
      Math.hypot(origin.x - this.lastTapAt.x, origin.y - this.lastTapAt.y) < TAP_PX * 2
    ) {
      this.lastTap = 0;
      this.onGesture({ type: "dbltap", x: origin.x, y: origin.y });
      return;
    }
    this.lastTap = now;
    this.lastTapAt = { x: origin.x, y: origin.y };
  }

  private syncPinch(): void {
    const pair = this.pair();
    if (!pair) {
      this.pinchDist = 0;
      return;
    }
    this.pinchDist = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y);
    this.pinchAngle = Math.atan2(pair[1].y - pair[0].y, pair[1].x - pair[0].x);
    this.pinchMid = {
      x: (pair[0].x + pair[1].x) / 2,
      y: (pair[0].y + pair[1].y) / 2,
    };
    this.scaleHold = 1;
    this.angleHold = 0;
  }

  private pair(): [Pt, Pt] | null {
    if (this.pts.size < 2) return null;
    const all = [...this.pts.values()];
    return [all[0], all[1]];
  }

  private localPtr(e: PointerEvent): Pt {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
}

function wrapAngle(d: number): number {
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export function isCoarsePointer(): boolean {
  return window.matchMedia("(pointer: coarse)").matches;
}

export function isCompactHud(): boolean {
  return window.matchMedia("(pointer: coarse), (max-width: 720px)").matches;
}
